import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';

@Component({
  selector: 'app-receipt-config-modal',
  templateUrl: './receipt-config-modal.component.html',
  styleUrl: './receipt-config-modal.component.scss'
})
export class ReceiptConfigModalComponent implements OnInit, OnChanges {

  // === INPUTS ===
  @Input() isVisible: boolean = false;
  @Input() receipt: any = null;
  @Input() receiptIndex: number = -1;
  @Input() totalReceipts: number = 0;
  @Input() feeTypes: any[] = [];
  @Input() receiptFormGroup: FormGroup | null = null;

  // === OUTPUTS ===
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();
  @Output() navigateToReceipt = new EventEmitter<number>();
  @Output() feeChanged = new EventEmitter<{receiptIndex: number, feeIndex: number}>();

  // === PROPIEDADES ===
  activeTab: 'basic' | 'fees' | 'summary' = 'basic';
  
  // === COMPUTED PROPERTIES ===
  get canNavigatePrevious(): boolean {
    return this.receiptIndex > 0;
  }

  get canNavigateNext(): boolean {
    return this.receiptIndex < this.totalReceipts - 1;
  }

  get receiptTitle(): string {
    if (!this.receipt) return '';
    const type = this.receipt.type === 'income' ? 'Ingreso' : 'Egreso';
    const icon = this.receipt.type === 'income' ? '💰' : '💸';
    return `${icon} ${type} #${this.receipt.id}`;
  }

  get originalAmount(): number {
    if (!this.receipt) return 0;
    return this.receipt.type === 'income' ? this.receipt.netAmount : this.receipt.totalToPay;
  }

  get bankAmount(): number {
    return this.receiptFormGroup?.get('bankAmount')?.value || 0;
  }

  get totalFees(): number {
    if (!this.receiptFormGroup) return 0;
    const feeControls = this.getFeeDetailsArray().controls;
    return feeControls.reduce((total: number, feeControl: any) => {
      const feeGroup = feeControl as FormGroup;
      return total + (feeGroup.get('amount')?.value || 0);
    }, 0);
  }

  get finalDifference(): number {
    return this.bankAmount - (this.originalAmount - this.totalFees);
  }

  get isBalanced(): boolean {
    return Math.abs(this.finalDifference) <= 0.01;
  }

  get configurationStatus(): 'complete' | 'partial' | 'empty' {
    if (!this.receiptFormGroup) return 'empty';
    
    const bankAmount = this.receiptFormGroup.get('bankAmount')?.value;
    const hasValidBankAmount = bankAmount && bankAmount > 0;
    
    if (hasValidBankAmount && this.isBalanced) return 'complete';
    if (hasValidBankAmount) return 'partial';
    return 'empty';
  }

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    // Inicialización si es necesaria
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible) {
      // Reset tab when modal opens
      this.activeTab = 'basic';
    }
  }

  // === NAVEGACIÓN ENTRE PESTAÑAS ===
  
  setActiveTab(tab: 'basic' | 'fees' | 'summary'): void {
    this.activeTab = tab;
  }

  // === NAVEGACIÓN ENTRE COMPROBANTES ===

  navigatePrevious(): void {
    if (this.canNavigatePrevious) {
      this.navigateToReceipt.emit(this.receiptIndex - 1);
    }
  }

  navigateNext(): void {
    if (this.canNavigateNext) {
      this.navigateToReceipt.emit(this.receiptIndex + 1);
    }
  }

  // === GESTIÓN DE FEES ===

  getFeeDetailsArray(): FormArray {
    if (!this.receiptFormGroup) return this.fb.array([]);
    return this.receiptFormGroup.get('feeDetails') as FormArray;
  }

  addFee(): void {
    const feeGroup = this.fb.group({
      feeTypeId: ['', Validators.required],
      requestAmount: ['', [Validators.required]],
      description: [''],
      amount: [0],
      porcentualAmount: [0]
    });

    this.getFeeDetailsArray().push(feeGroup);
  }

  removeFee(feeIndex: number): void {
    this.getFeeDetailsArray().removeAt(feeIndex);
    this.calculateTotals();
  }

  onFeeChange(feeIndex: number): void {
    this.calculateFeeAmount(feeIndex);
    this.calculateTotals();
    this.feeChanged.emit({ receiptIndex: this.receiptIndex, feeIndex });
  }

  private calculateFeeAmount(feeIndex: number): void {
    const feeGroup = this.getFeeDetailsArray().at(feeIndex) as FormGroup;
    const requestAmount = feeGroup.get('requestAmount')?.value;

    if (!requestAmount) {
      feeGroup.patchValue({ amount: 0, porcentualAmount: 0 });
      return;
    }

    if (requestAmount.includes('%')) {
      // Es porcentaje
      const percentage = parseFloat(requestAmount.replace('%', '').trim());
      if (!isNaN(percentage)) {
        const amount = (percentage / 100) * this.originalAmount;
        feeGroup.patchValue({ 
          amount: amount,
          porcentualAmount: percentage
        });
      }
    } else {
      // Es valor fijo
      const amount = parseFloat(requestAmount.trim());
      if (!isNaN(amount)) {
        const percentage = this.originalAmount > 0 ? (amount / this.originalAmount) * 100 : 0;
        feeGroup.patchValue({ 
          amount: amount,
          porcentualAmount: percentage
        });
      }
    }
  }

  calculateTotals(): void {
    // Emitir evento para que el componente padre actualice los totales globales
    this.save.emit(this.receiptFormGroup?.value);
  }

  // === ACCIONES DEL MODAL ===

  onSave(): void {
    if (this.receiptFormGroup?.valid) {
      this.save.emit(this.receiptFormGroup.value);
      this.close.emit();
    }
  }

  onClose(): void {
    this.close.emit();
  }

  // === UTILIDADES ===

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  formatDate(date: string): string {
    return new Date(date).toLocaleDateString('es-CO');
  }

  getStatusColor(): string {
    switch (this.configurationStatus) {
      case 'complete': return 'text-green-600';
      case 'partial': return 'text-yellow-600';
      case 'empty': return 'text-gray-400';
      default: return 'text-gray-400';
    }
  }

  getStatusIcon(): string {
    switch (this.configurationStatus) {
      case 'complete': return '✅';
      case 'partial': return '⚠️';
      case 'empty': return '⏳';
      default: return '⏳';
    }
  }

  getStatusText(): string {
    switch (this.configurationStatus) {
      case 'complete': return 'Configurado y Balanceado';
      case 'partial': return 'Configurado con Diferencias';
      case 'empty': return 'Pendiente de Configurar';
      default: return 'Sin Configurar';
    }
  }
}
